import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Button,
  Card,
  Spinner,
  Alert,
  Badge,
  Row,
  Col,
  Tooltip,
  OverlayTrigger,
  Form,
  InputGroup,
} from 'react-bootstrap';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import axios from 'axios';
import BASE_URL from '../config';
import './AdminLayout.css';
import {
  FaMapMarkerAlt,
  FaMapPin,
  FaCar,
  FaShareAlt,
  FaQrcode,
  FaLink,
  FaWhatsapp,
  FaEnvelope,
  FaExternalLinkAlt,
  FaCopy,
  FaPhone,
  FaRegClock,
  FaRoute,
} from 'react-icons/fa';

// Fix for Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

// Custom icons for different markers
const createIcon = (color, icon = null) => {
  return L.divIcon({
    className: 'custom-div-icon',
    html: `<div style="background-color: ${color}; width: 30px; height: 30px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; color: white; font-size: 14px; font-weight: bold;">${
      icon || ''
    }</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
};

const pickupIcon = createIcon('#28a745', '📍'); // Green with pin icon
const dropIcon = createIcon('#dc3545', '🏁'); // Red with flag icon
const driverIcon = createIcon('#007bff', '🚗'); // Blue with car icon

// Component to fit map bounds
const FitBounds = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (bounds && bounds.length > 0) {
      const leafletBounds = L.latLngBounds(bounds);
      map.fitBounds(leafletBounds, { padding: [50, 50], maxZoom: 16 });
    }
  }, [bounds, map]);

  return null;
};

// Component to update map center imperatively
const MapUpdater = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    map.flyTo(center, zoom);
  }, [map, center, zoom]);
  return null;
};

// Function to reverse geocode coordinates to address
const geocodeLatLng = async (lat, lng) => {
  try {
    const res = await axios.get(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&accept-language=en`
    );
    return res.data.display_name || 'Address not found';
  } catch (err) {
    console.error('Geocoding failed:', err);
    return 'Geocoding failed';
  }
};

// Function to calculate estimated time of arrival
const calculateETA = (distance, speed = 40) => {
  const timeInHours = distance / speed;
  const hours = Math.floor(timeInHours);
  const minutes = Math.round((timeInHours - hours) * 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

// Function to calculate distance between two points in km
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) *
      Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  return d;
};

const deg2rad = (deg) => deg * (Math.PI / 180);

const OrderTracking = ({
  show,
  handleClose,
  order,
  driverId,
  isPublicView = false,
}) => {
  console.log('order', order);
  const [currentDriverLocation, setCurrentDriverLocation] = useState(null);
  const [driverAddress, setDriverAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [trackingInterval, setTrackingInterval] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [distance, setDistance] = useState(null);
  const [eta, setEta] = useState(null);
  const intervalRef = useRef(null);

  const defaultCenter = [25.2048, 55.2708];
  const mapStyle = { height: '300px', width: '100%' };

  const fetchDriverLocation = async () => {
    if (!driverId) return;

    setLoading(true);
    setError(null);

    try {
      const res = await axios.get(`${BASE_URL}/driver-locations`, {
        params: { driver_id: driverId },
      });
      const driversData = res.data?.data?.drivers;

      if (Array.isArray(driversData)) {
        const driver = driversData.find((d) => d.id === parseInt(driverId));
        if (driver && driver.location) {
          const lat = parseFloat(driver.location.lat);
          const lng = parseFloat(driver.location.lng);
          setCurrentDriverLocation({
            lat,
            lng,
            timestamp: driver.location.timestamp,
          });

          const address = await geocodeLatLng(lat, lng);
          setDriverAddress(address);
          setLastUpdated(new Date());

          if (order?.delivery_lat && order?.delivery_lng) {
            const dropLat = parseFloat(order.delivery_lat);
            const dropLng = parseFloat(order.delivery_lng);
            const dist = calculateDistance(lat, lng, dropLat, dropLng);
            setDistance(dist);
            setEta(calculateETA(dist));
          }
        } else {
          setCurrentDriverLocation(null);
          setDriverAddress('');
          setDistance(null);
          setEta(null);
        }
      } else {
        setCurrentDriverLocation(null);
        setDriverAddress('');
        setDistance(null);
        setEta(null);
      }
    } catch (err) {
      console.error('Error fetching driver location:', err);
      setError('Failed to fetch driver location');
      setCurrentDriverLocation(null);
      setDriverAddress('');
      setDistance(null);
      setEta(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchDriverLocation();
      intervalRef.current = setInterval(fetchDriverLocation, 30000);
      setTrackingInterval(intervalRef.current);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        setTrackingInterval(null);
      }
    };
  }, [show, driverId]);

  const getMarkerPositions = () => {
    const positions = [];

    if (order?.pickup_lat && order?.pickup_lng) {
      positions.push([
        parseFloat(order.pickup_lat),
        parseFloat(order.pickup_lng),
      ]);
    }

    if (order?.delivery_lat && order?.delivery_lng) {
      positions.push([
        parseFloat(order.delivery_lat),
        parseFloat(order.delivery_lng),
      ]);
    }

    if (currentDriverLocation) {
      positions.push([currentDriverLocation.lat, currentDriverLocation.lng]);
    }

    return positions;
  };

  const getRouteCoordinates = () => {
    if (!order) return [];

    const route = [];

    if (order.pickup_lat && order.pickup_lng) {
      route.push([parseFloat(order.pickup_lat), parseFloat(order.pickup_lng)]);
    }

    if (currentDriverLocation) {
      route.push([currentDriverLocation.lat, currentDriverLocation.lng]);
    }

    if (order.delivery_lat && order.delivery_lng) {
      route.push([
        parseFloat(order.delivery_lat),
        parseFloat(order.delivery_lng),
      ]);
    }

    return route;
  };

  // ===== UPDATED: Simple share link with only driverId =====
  const handleShareTracking = () => {
    if (!order) {
      setError('No tracking data available to share.');
      return;
    }

    const baseUrl = window.location.origin;
    const trackingUrl = `${baseUrl}/track-order/${order.id}?driverId=${driverId}`;
    const message = `Track your order live: ${trackingUrl}`;

    setShareUrl(trackingUrl);
    setShareMessage(message);
    setQrCodeUrl(
      `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
        trackingUrl
      )}`
    );
    setShowShareModal(true);
  };
  // ==========================================================

  const handleCopyToClipboard = () => {
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => alert('Link copied to clipboard!'))
      .catch((err) => {
        console.error('Failed to copy: ', err);
        alert('Failed to copy link to clipboard.');
      });
  };

  const handleWebShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: `Order Tracking - #${order.po_number || order.id}`,
          text: shareMessage,
          url: shareUrl,
        })
        .catch((err) => console.log('Error sharing', err));
    } else {
      alert('Web Share API not supported on this device.');
    }
  };

  const handleOpenTrackingLink = () => {
    window.open(shareUrl, '_blank');
  };

  const markerPositions = getMarkerPositions();
  const routeCoordinates = getRouteCoordinates();

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size='lg'
        fullscreen='md-down'
        className='order-tracking-modal'
      >
        <Modal.Header closeButton className='bg-primary text-white'>
          <Modal.Title>
            <FaMapMarkerAlt className='me-2' /> Track Order #
            {order?.po_number || order?.id}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{ maxHeight: 'calc(90vh - 120px)', overflowY: 'auto' }}
        >
          {!isPublicView && (
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <div>
                <strong>Driver Location:</strong>
                <span className='ms-2 text-muted'>
                  {currentDriverLocation
                    ? `${currentDriverLocation.lat.toFixed(
                        4
                      )}, ${currentDriverLocation.lng.toFixed(4)}`
                    : 'Fetching...'}
                </span>
                {driverAddress && (
                  <>
                    <br />
                    <small className='text-muted'>{driverAddress}</small>
                  </>
                )}
                {lastUpdated && (
                  <>
                    <br />
                    <small className='text-muted'>
                      <FaRegClock className='me-1' />
                      Last updated: {lastUpdated.toLocaleTimeString()}
                    </small>
                  </>
                )}
              </div>
              <Button variant='success' onClick={handleShareTracking}>
                <FaShareAlt className='me-2' /> Share Tracking
              </Button>
            </div>
          )}

          {loading && (
            <div className='text-center my-4'>
              <Spinner animation='border' />
              <div>Loading driver location...</div>
            </div>
          )}

          {error && <Alert variant='danger'>{error}</Alert>}

          {order && (
            <div>
              <Row className='mb-3'>
                <Col md={4}>
                  <Card className='h-100 border-success'>
                    <Card.Body>
                      <Card.Title className='text-success'>
                        <FaMapPin className='me-2' /> Pickup
                      </Card.Title>
                      <Card.Text>
                        <small>
                          {order?.pickup_address ||
                            order.showroom?.address ||
                            'N/A'}
                        </small>
                        <br />
                        <small className='text-muted'>
                          {order.pickup_lat && order.pickup_lng
                            ? `${parseFloat(order.pickup_lat).toFixed(
                                4
                              )}, ${parseFloat(order.pickup_lng).toFixed(4)}`
                            : 'N/A'}
                        </small>
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className='h-100 border-danger'>
                    <Card.Body>
                      <Card.Title className='text-danger'>
                        <FaMapPin className='me-2' /> Drop
                      </Card.Title>
                      <Card.Text>
                        <small>
                          {order.delivery_address ||
                            order.customer?.address ||
                            'N/A'}
                        </small>
                        <br />
                        <small className='text-muted'>
                          {order.delivery_lat && order.delivery_lng
                            ? `${parseFloat(order.delivery_lat).toFixed(
                                4
                              )}, ${parseFloat(order.delivery_lng).toFixed(4)}`
                            : 'N/A'}
                        </small>
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className='h-100 border-primary'>
                    <Card.Body>
                      <Card.Title className='text-primary'>
                        <FaCar className='me-2' /> Driver
                      </Card.Title>
                      <Card.Text>
                        <small>{order?.driver?.name || (driverId ? `#${driverId}` : 'Unassigned')}</small>
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {distance && eta && (
                <Row className='mb-3'>
                  <Col md={6}>
                    <Card className='border-info'>
                      <Card.Body className='text-center'>
                        <Card.Title className='text-info'>
                          <FaRoute className='me-2' /> Distance
                        </Card.Title>
                        <Card.Text>
                          <span className='fs-4'>{distance.toFixed(1)} km</span>
                        </Card.Text>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className='border-warning'>
                      <Card.Body className='text-center'>
                        <Card.Title className='text-warning'>
                          <FaRegClock className='me-2' /> ETA
                        </Card.Title>
                        <Card.Text>
                          <span className='fs-4'>{eta}</span>
                        </Card.Text>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              <div style={mapStyle}>
                <MapContainer
                  center={
                    markerPositions.length > 0
                      ? markerPositions[0]
                      : defaultCenter
                  }
                  zoom={markerPositions.length > 0 ? 13 : 5}
                  style={mapStyle}
                  zoomControl={false}
                  scrollWheelZoom={false}
                  doubleClickZoom={false}
                  touchZoom={false}
                  boxZoom={false}
                  keyboard={false}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                    language='en'
                    detectRetina={true}
                  />

                  {markerPositions.length > 0 && (
                    <FitBounds bounds={markerPositions} />
                  )}

                  {order.pickup_lat && order.pickup_lng && (
                    <Marker
                      position={[
                        parseFloat(order.pickup_lat),
                        parseFloat(order.pickup_lng),
                      ]}
                      icon={pickupIcon}
                    >
                      <Popup>
                        <strong>Pickup</strong>
                        <br />
                        <small>{order.pickup_address}</small>
                      </Popup>
                    </Marker>
                  )}

                  {order.delivery_lat && order.delivery_lng && (
                    <Marker
                      position={[
                        parseFloat(order.delivery_lat),
                        parseFloat(order.delivery_lng),
                      ]}
                      icon={dropIcon}
                    >
                      <Popup>
                        <strong>Drop</strong>
                        <br />
                        <small>{order.delivery_address}</small>
                      </Popup>
                    </Marker>
                  )}

                  {currentDriverLocation && (
                    <Marker
                      position={[
                        currentDriverLocation.lat,
                        currentDriverLocation.lng,
                      ]}
                      icon={driverIcon}
                    >
                      <Popup>
                        <strong>Driver Location</strong>
                        <br />
                        <small>
                          {driverAddress || 'Address not available'}
                        </small>
                        <br />
                        <small>
                          Last updated:{' '}
                          {new Date(
                            currentDriverLocation.timestamp
                          ).toLocaleString()}
                        </small>
                      </Popup>
                    </Marker>
                  )}

                  {routeCoordinates.length > 1 && (
                    <Polyline
                      positions={routeCoordinates}
                      color='#007bff'
                      weight={4}
                      opacity={0.7}
                    />
                  )}
                </MapContainer>
              </div>

              <div className='mt-3 d-flex justify-content-center'>
                <div className='d-flex align-items-center me-4'>
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#28a745',
                      borderRadius: '50%',
                      marginRight: '8px',
                    }}
                  ></div>
                  <span>Pickup</span>
                </div>
                <div className='d-flex align-items-center me-4'>
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#dc3545',
                      borderRadius: '50%',
                      marginRight: '8px',
                    }}
                  ></div>
                  <span>Drop</span>
                </div>
                <div className='d-flex align-items-center'>
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#007bff',
                      borderRadius: '50%',
                      marginRight: '8px',
                    }}
                  ></div>
                  <span>Driver</span>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Share Tracking Modal */}
      <Modal
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        size='xl'
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaShareAlt className='me-2' /> Share Order Tracking
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Share Link & QR Code Section */}
          <Row className='mb-4'>
            <Col md={8}>
              <Form.Group className='mb-3'>
                <Form.Label>Tracking Link</Form.Label>
                <InputGroup>
                  <Form.Control type='text' value={shareUrl} readOnly />
                  <Button
                    variant='outline-secondary'
                    onClick={handleCopyToClipboard}
                  >
                    <FaCopy />
                  </Button>
                  <Button
                    variant='outline-primary'
                    onClick={handleOpenTrackingLink}
                  >
                    <FaExternalLinkAlt />
                  </Button>
                </InputGroup>
              </Form.Group>

              <Form.Group className='mb-3'>
                <Form.Label>Share Message</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={4}
                  value={shareMessage}
                  readOnly
                />
              </Form.Group>

              <div className='d-grid gap-2'>
                <Button variant='primary' onClick={handleWebShare}>
                  <FaShareAlt className='me-2' /> Share via Device Options
                </Button>

                <Button
                  variant='outline-success'
                  href={`https://wa.me/?text=${encodeURIComponent(
                    shareMessage
                  )}`}
                  target='_blank'
                >
                  <FaWhatsapp className='me-2' /> Share on WhatsApp
                </Button>

                <Button
                  variant='outline-info'
                  href={`mailto:?subject=Order Tracking&body=${encodeURIComponent(
                    shareMessage
                  )}`}
                  target='_blank'
                >
                  <FaEnvelope className='me-2' /> Share via Email
                </Button>
              </div>
            </Col>
            <Col md={4} className='text-center'>
              <div className='mb-3'>
                <h5>QR Code</h5>
                <p>Scan to track on mobile</p>
                {qrCodeUrl ? (
                  <img
                    src={qrCodeUrl}
                    alt='QR Code'
                    className='img-fluid'
                    style={{ maxWidth: '200px' }}
                  />
                ) : (
                  <div
                    className='d-flex justify-content-center align-items-center bg-light'
                    style={{ height: '200px', width: '200px' }}
                  >
                    <Spinner animation='border' />
                  </div>
                )}
              </div>
              <Button
                variant='outline-primary'
                onClick={handleCopyToClipboard}
                className='w-100'
              >
                <FaLink className='me-2' /> Copy Link
              </Button>
            </Col>
          </Row>

          <hr />

          {/* Order Details Section */}
          <h5 className='mb-3'>📋 Order Details</h5>
          
          {/* Attachments */}
          <div className='mb-3 p-2 bg-light rounded'>
            <h6>Attachments:</h6>
            {order?.attachments && (order?.attachments?.data?.length > 0 || order?.attachments?.length > 0) ? (
              <div className='d-flex flex-wrap gap-2'>
                {(Array.isArray(order.attachments?.data) ? order.attachments.data : order.attachments).map((file, idx) => (
                  <Badge key={idx} bg='info'>
                    📎 {file.type?.replace(/_/g, ' ') || `File ${idx + 1}`}
                  </Badge>
                ))}
              </div>
            ) : (
              <span className='text-muted'>—</span>
            )}
          </div>

          {/* Customer / Vendor Details */}
          <div className='mb-3 p-2 bg-light rounded'>
            <h6>👤 Customer / Vendor Details</h6>
            <div>
              <strong>Vendor:</strong> <span>{order?.vendor?.name || '—'}</span>
            </div>
            <div>
              <strong>Phone:</strong> <span>{order?.vendor?.phone || order?.customer?.phone || '—'}</span>
            </div>
          </div>

          {/* Pickup & Drop Location */}
          <div className='mb-3 p-2 bg-light rounded'>
            <h6>🗺️ Pickup & Drop Location</h6>
            <div>
              <strong>Pickup:</strong> <span>{order?.pickup_address || '—'}</span>
            </div>
            <div>
              <strong>Delivery:</strong> <span>{order?.delivery_address || '—'}</span>
            </div>
            <div>
              <strong>Driver:</strong> <span>{order?.driver?.name || (driverId ? `#${driverId}` : 'Unassigned')}</span>
            </div>
            <div>
              <strong>Showroom:</strong> <span>{order?.showroom?.name || '—'}</span>
            </div>
          </div>

          {/* Remarks */}
          <div className='p-2 bg-light rounded'>
            <h6>📝 Remarks</h6>
            <span>{order?.notes || order?.remarks || '—'}</span>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowShareModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default OrderTracking;
